import React, { Fragment, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { VisuallyHidden } from '@coursera/cds-core';

import _t from 'i18n!nls/discussions';

import type { InputVariables, ResponseVariables, YourActivityTabDataProviderProps } from './__types__';

const GetYourActivityQuery = gql`
  query GetYourActivityQuery($input: InputVariables!) {
    GetYourActivityResponse(input: $input)
      @rest(type: "GetYourActivityResponse", path: "grpc/forum/post/v1/ForumPostSearchAPI/Search", method: "POST") {
      postResults
      nextPageToken
      totalHits
    }
  }
`;

const YourActivityTabDataProvider: React.FC<YourActivityTabDataProviderProps> = ({
  courseId,
  userId,
  currentPage,
  hasBeenVisited,
  children,
}) => {
  const [notificationText, setNotification] = useState('');

  const { data, loading, error } = useQuery<ResponseVariables, InputVariables>(GetYourActivityQuery, {
    variables: {
      input: {
        course_id: courseId,
        request: {
          sort: 'SEARCH_SORT_CREATED_AT_DESC',
          filter: {
            search_filter_type: 'FILTER_TYPE_MY_POSTS_USER_ID',
            search_filter_value: userId,
          },
        },
        page_size: 15,
        page_token: ((currentPage - 1) * 15).toString(),
      },
    },
    skip: !courseId,
    context: { clientName: 'gatewayGql' },
  });

  const defaultResponse = { postResults: [], nextPageToken: '', totalHits: '0' };
  const yourActivityResponse = data?.GetYourActivityResponse || defaultResponse;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const setAsyncNotification = (func: () => void) => {
      timeoutId = setTimeout(func, 0);
    };

    if (loading) {
      setAsyncNotification(() => setNotification(_t('Loading posts')));
    } else if (!yourActivityResponse.postResults.length) {
      setAsyncNotification(() => setNotification(_t('No posts yet')));
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading, yourActivityResponse, hasBeenVisited]);

  return (
    <Fragment>
      <VisuallyHidden aria-live="assertive" aria-atomic="true" role="alert">
        {!hasBeenVisited && yourActivityResponse.postResults.length ? (
          <FormattedMessage
            message={_t('Loaded {numOfPosts} {numOfPosts, plural, =1 {post} other {posts}}')}
            numOfPosts={yourActivityResponse.postResults.length}
          />
        ) : (
          notificationText
        )}
      </VisuallyHidden>
      {children({ loading, error, data: yourActivityResponse })}
    </Fragment>
  );
};

export default YourActivityTabDataProvider;
